export type ScorecardStatus = 'pending' | 'processing' | 'completed' | 'failed';

export type CosmoCategory = 'core_usage' | 'product_identity' | 'context' | 'audience' | 'compatibility';

export const COSMO_CATEGORY_LABELS: Record<CosmoCategory, string> = {
  core_usage: 'Core Usage',
  product_identity: 'Product Identity',
  context: 'Context',
  audience: 'Audience',
  compatibility: 'Compatibility',
};

/**
 * AUT-310: the scorecard model returns `noChangesNeeded` and `recommendation` in the same
 * response but decides them independently, so a question can carry a "No changes needed."
 * recommendation while the flag stays false -- and then reads as a real gap. Either signal
 * is enough. Mirrors SeoStrategyGapPoolService::recommendationSaysNoChangesNeeded().
 */
export const isNoChangesNeeded = (question: {
  noChangesNeeded?: boolean;
  recommendation?: string;
}): boolean =>
  Boolean(question.noChangesNeeded) ||
  (question.recommendation ?? '').trim().toLowerCase().startsWith('no changes needed');

/**
 * AUT-308: a consolidated SEO Strategy recommendation can carry a combined category the
 * model writes itself (e.g. "context/compatibility"), which is not a key in
 * COSMO_CATEGORY_LABELS and so used to render raw. Each segment is formatted on its own
 * and rejoined; a segment the map doesn't know still gets title-cased rather than shown
 * as a raw relation key.
 */
export const formatCosmoCategory = (category: string): string =>
  category
    .split('/')
    .map((segment) => {
      const key = segment.trim();

      return (
        COSMO_CATEGORY_LABELS[key as CosmoCategory] ??
        key.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase())
      );
    })
    .filter(Boolean)
    .join('/');

export const COSMO_CATEGORY_ORDER: CosmoCategory[] = [
  'core_usage',
  'product_identity',
  'context',
  'audience',
  'compatibility',
];

export interface CosmoQuestion {
  id: number;
  relationKey: string;
  question: string;
  category: CosmoCategory;
  score: number;
  status: 'strong' | 'moderate' | 'weak' | 'missing' | 'not_applicable';
  finding: string;
  recommendation: string;
  evidenceSnippet: string;
  previousScore?: number | null;
  changeNote?: string | null;
  notApplicable?: boolean;
  noChangesNeeded?: boolean;
  hasRunningTest?: boolean;
}

export interface CategoryBreakdownItem {
  category: CosmoCategory;
  score: number | null;
  questionCount: number;
  applicableCount?: number;
  previousScore?: number | null;
}

export interface StrategicSummary {
  listings: string;
  aPlusContent: string;
  brandStore: string;
}

export interface SPIResult {
  asin: string;
  productTitle: string;
  productBrand: string;
  mainImageUrl?: string;
  overallScore: number;
  overallGrade: string;
  categoryBreakdown: CategoryBreakdownItem[];
  questions: CosmoQuestion[];
  strategicSummary: StrategicSummary;
  previousOverallScore?: number | null;
  previousScorecardId?: number | null;
}

interface ScorecardMeta {
  id: number;
  asin: string;
  createdAt: string;
  completedAt: string | null;
  errorMessage: string | null;
}

export type ScorecardResponse =
  | (ScorecardMeta & { status: 'pending' | 'processing'; step?: string })
  | (ScorecardMeta & SPIResult & { status: 'completed'; completedAt: string })
  | (ScorecardMeta & { status: 'failed' });

export interface ScorecardListItem {
  id: number;
  asin: string;
  status: ScorecardStatus;
  overallScore?: number;
  overallGrade?: string;
  createdAt: string;
  completedAt?: string;
}

export interface CreateScorecardParams {
  asin: string;
  brandId?: number;
  country?: 'us';
}

export interface CreateScorecardResponse {
  id: number;
  status: ScorecardStatus;
  asin: string;
  createdAt: string;
}
